/**
 * KOPS-680: Communications Tab — SMS Threads + Voice Call Logs
 * Displays Maya brokerage line conversations and call history
 */

import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Table, Badge, Spinner } from 'react-bootstrap';
import axiosInstance from '../../helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faPhoneAlt } from '@fortawesome/free-solid-svg-icons';

const CommunicationsTab: React.FC = () => {
  const [commTab, setCommTab] = useState<'sms' | 'calls'>('sms');
  const [threads, setThreads] = useState<any[]>([]);
  const [calls, setCalls] = useState<any[]>([]);
  const [commStats, setCommStats] = useState<any>(null);
  const [selectedThread, setSelectedThread] = useState<any>(null);
  const [threadMessages, setThreadMessages] = useState<any[]>([]);
  const [commLoading, setCommLoading] = useState(true);

  useEffect(() => {
    const loadComms = async () => {
      setCommLoading(true);
      try {
        const [threadsRes, callsRes, statsRes] = await Promise.all([
          axiosInstance.get('/twilio/communications/threads'),
          axiosInstance.get('/twilio/communications/calls'),
          axiosInstance.get('/twilio/communications/stats')
        ]);
        setThreads(threadsRes.data.threads || []);
        setCalls(callsRes.data.calls || []);
        setCommStats(statsRes.data.stats || null);
      } catch (err) {
        console.error('Failed to load communications:', err);
      }
      setCommLoading(false);
    };
    loadComms();
    const interval = setInterval(loadComms, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadThread = async (phone: string) => {
    try {
      const res = await axiosInstance.get(`/twilio/communications/thread/${encodeURIComponent(phone)}`);
      setSelectedThread({ phone, contact: res.data.contact });
      setThreadMessages(res.data.messages || []);
    } catch (err) {
      console.error('Failed to load thread:', err);
    }
  };

  const formatPhone = (p: string) => {
    const d = (p || '').replace(/\D/g, '').slice(-10);
    return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p;
  };

  const timeAgo = (d: string) => {
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div>
      {/* Stats Header */}
      {commStats && (
        <Row className="mb-3">
          <Col md={3}>
            <Card style={{ background: '#0F1E35', color: 'white', border: 'none' }}>
              <Card.Body className="text-center py-2">
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#E8B44A' }}>{commStats.sms?.uniqueTexters || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>SMS Contacts</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ background: '#0F1E35', color: 'white', border: 'none' }}>
              <Card.Body className="text-center py-2">
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#E8B44A' }}>{commStats.sms?.totalMessages || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>Total Messages</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ background: '#0F1E35', color: 'white', border: 'none' }}>
              <Card.Body className="text-center py-2">
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#E8B44A' }}>{commStats.calls?.totalCalls || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>Voice Calls</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ background: '#0F1E35', color: 'white', border: 'none' }}>
              <Card.Body className="text-center py-2">
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#E8B44A' }}>{commStats.sms?.last24h || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>Last 24 Hours</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Sub-tabs */}
      <div className="d-flex mb-3" style={{ gap: '8px' }}>
        <Button
          size="sm"
          variant={commTab === 'sms' ? 'dark' : 'outline-secondary'}
          onClick={() => { setCommTab('sms'); setSelectedThread(null); }}
          style={commTab === 'sms' ? { background: '#0F1E35', borderColor: '#0F1E35' } : {}}
        >
          <FontAwesomeIcon icon={faComments} className="me-1" /> SMS Conversations
        </Button>
        <Button
          size="sm"
          variant={commTab === 'calls' ? 'dark' : 'outline-secondary'}
          onClick={() => setCommTab('calls')}
          style={commTab === 'calls' ? { background: '#0F1E35', borderColor: '#0F1E35' } : {}}
        >
          <FontAwesomeIcon icon={faPhoneAlt} className="me-1" /> Voice Calls
        </Button>
      </div>

      {commLoading ? (
        <div className="text-center py-5"><Spinner animation="border" size="sm" /> Loading...</div>
      ) : (
        <>
          {/* SMS Thread List */}
          {commTab === 'sms' && !selectedThread && (
            <Card style={{ border: '1px solid #e0e0e0' }}>
              <Card.Body className="p-0">
                {threads.length === 0 ? (
                  <div className="text-center py-5" style={{ color: '#888' }}>
                    <FontAwesomeIcon icon={faComments} size="2x" className="mb-2" style={{ opacity: 0.3 }} />
                    <p>No SMS conversations yet. Texts to (602) 860-6110 will appear here.</p>
                  </div>
                ) : (
                  <Table hover className="mb-0" style={{ fontSize: '13px' }}>
                    <thead style={{ background: '#f8f9fa' }}>
                      <tr>
                        <th>Contact</th>
                        <th>Messages</th>
                        <th>Last Activity</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {threads.map((t: any, i: number) => (
                        <tr key={i} style={{ cursor: 'pointer' }} onClick={() => loadThread(t.phone)}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{t.contact_name || formatPhone(t.phone)}</div>
                            {t.contact_name && <div style={{ fontSize: '11px', color: '#888' }}>{formatPhone(t.phone)}</div>}
                            {t.contact_email && <div style={{ fontSize: '11px', color: '#C9952A' }}>{t.contact_email}</div>}
                          </td>
                          <td><Badge bg="secondary">{Number(t.message_count)}</Badge></td>
                          <td style={{ color: '#888', fontSize: '12px' }}>{timeAgo(t.last_message_at)}</td>
                          <td><FontAwesomeIcon icon={faComments} style={{ color: '#C9952A' }} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}

          {/* SMS Thread Detail */}
          {commTab === 'sms' && selectedThread && (
            <Card style={{ border: '1px solid #e0e0e0' }}>
              <Card.Header style={{ background: '#0F1E35', color: 'white', padding: '10px 16px' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <Button size="sm" variant="link" style={{ color: '#E8B44A', padding: 0, marginRight: '12px' }} onClick={() => setSelectedThread(null)}>
                      &larr; Back
                    </Button>
                    <strong>{selectedThread.contact?.full_name || formatPhone(selectedThread.phone)}</strong>
                    {selectedThread.contact?.email && <span style={{ marginLeft: '12px', fontSize: '12px', opacity: 0.7 }}>{selectedThread.contact.email}</span>}
                  </div>
                  <span style={{ fontSize: '12px', opacity: 0.7 }}>{formatPhone(selectedThread.phone)}</span>
                </div>
              </Card.Header>
              <Card.Body style={{ maxHeight: '500px', overflowY: 'auto', background: '#f8f9fa', padding: '16px' }}>
                {threadMessages.map((msg: any, i: number) => (
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: msg.direction === 'outbound' ? 'flex-end' : 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      maxWidth: '75%',
                      padding: '8px 12px',
                      borderRadius: '12px',
                      background: msg.direction === 'outbound' ? '#0F1E35' : 'white',
                      color: msg.direction === 'outbound' ? 'white' : '#333',
                      border: msg.direction === 'outbound' ? 'none' : '1px solid #e0e0e0',
                      fontSize: '13px'
                    }}>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.body}</div>
                      <div style={{
                        fontSize: '10px',
                        opacity: 0.6,
                        marginTop: '4px',
                        textAlign: 'right'
                      }}>
                        {msg.direction === 'outbound' ? 'Maya' : 'Customer'} &middot; {new Date(msg.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
                {threadMessages.length === 0 && <div className="text-center py-3" style={{ color: '#888' }}>No messages in this thread</div>}
              </Card.Body>
            </Card>
          )}

          {/* Calls Tab */}
          {commTab === 'calls' && (
            <Card style={{ border: '1px solid #e0e0e0' }}>
              <Card.Body className="p-0">
                {calls.length === 0 ? (
                  <div className="text-center py-5" style={{ color: '#888' }}>
                    <FontAwesomeIcon icon={faPhoneAlt} size="2x" className="mb-2" style={{ opacity: 0.3 }} />
                    <p>No call records yet. Calls to (602) 860-6110 will appear here.</p>
                  </div>
                ) : (
                  <Table hover className="mb-0" style={{ fontSize: '13px' }}>
                    <thead style={{ background: '#f8f9fa' }}>
                      <tr>
                        <th>Caller</th>
                        <th>Duration</th>
                        <th>Summary</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calls.map((c: any, i: number) => {
                        const callerPhone = c.direction === 'inbound' ? c.from_number : c.to_number;
                        const dur = c.duration ? `${Math.floor(c.duration / 60)}:${String(c.duration % 60).padStart(2, '0')}` : '\u2014';
                        return (
                          <tr key={i}>
                            <td>
                              <div style={{ fontWeight: 600 }}>{c.contact_name || formatPhone(callerPhone)}</div>
                              {c.contact_name && <div style={{ fontSize: '11px', color: '#888' }}>{formatPhone(callerPhone)}</div>}
                            </td>
                            <td><Badge bg={c.duration > 60 ? 'success' : 'secondary'}>{dur}</Badge></td>
                            <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#555' }}>
                              {c.transcript_summary || '\u2014'}
                            </td>
                            <td style={{ color: '#888', fontSize: '12px' }}>{timeAgo(c.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default CommunicationsTab;
